<template>
  <v-container fluid style="background: #eaedf1">
    <v-card flat :loading="campaignLoading" class="pa-4">
      <v-row class="d-flex flex-row justify-space-between align-center">
        <v-col md="1" lg="2">
          <v-btn icon text @click="$router.back()" class="ml-2">
            <v-icon v-text="icons.mdiArrowLeft" size="30"></v-icon>
          </v-btn>
        </v-col>
        <v-col md="8">
          <v-card-actions class="justify-center">
            <v-btn text @click="prev">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-item-group v-model="currentTab" class="text-center" mandatory>
              <v-card-title class="subtitle-1 font-weight-bold px-6">
                {{ translations[`campaignInfo_${sections[currentTab].value}`][lang] }}
              </v-card-title>
              <v-item v-for="n in sections" :key="`btn-${n.value}`" v-slot="{ active, toggle }">
                <v-tooltip v-if="sections[currentTab].value != n.value" color="secondary" bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn :input-value="active" icon v-on="on" @click="toggle">
                      <v-icon v-text="n.icon"></v-icon>
                    </v-btn>
                  </template>

                  <span class="font-weight-regular">
                    {{ translations[`campaignInfo_${n.value}`][lang] }}</span
                  >
                </v-tooltip>
                <v-btn v-else :input-value="active" icon @click="toggle">
                  <v-icon v-text="n.icon"></v-icon>
                </v-btn>
              </v-item>
            </v-item-group>
            <v-btn text @click="next">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-card-actions>
        </v-col>
        <v-col md="3" lg="2">
          <v-btn
            v-if="campaignType && campaignType.name !== 'draw'"
            color="secondary"
            class="text-capitalize px-5"
            depressed
            v-text="translations.createNewDraw[lang]"
            @click="openNewDrawDialog()"
          ></v-btn>
        </v-col>
      </v-row>
      <v-skeleton-loader v-if="campaignLoading" class="pa-4" elevation="0" style="width: 100%">
        <template v-slot:default>
          <v-row>
            <v-col cols="12" class="d-flex justify-center">
              <v-skeleton-loader
                style="width: 50%"
                type="heading, table-cell"
                class="mb-4"
              ></v-skeleton-loader>
            </v-col>
            <v-col md="12" v-for="n in [1, 2, 3, 4, 5, 6]" :key="`skeleton-${n}`" class="py-0 my-0">
              <v-col cols="12" class="d-flex justify-center py-0 my-0">
                <v-skeleton-loader
                  style="width: 100%"
                  type="table-cell"
                  class="mb-4"
                ></v-skeleton-loader>
                <v-skeleton-loader
                  style="width: 100%"
                  type="table-cell"
                  class="mb-4"
                ></v-skeleton-loader>
              </v-col>
              <v-skeleton-loader type="divider" class="mb-1 mt-n4"></v-skeleton-loader>
            </v-col>
          </v-row>
        </template>
      </v-skeleton-loader>
      <v-window v-else v-model="currentTab">
        <v-window-item>
          <GeneralInfo />
        </v-window-item>
        <v-window-item v-if="hasReward">
          <RewardInfo />
        </v-window-item>
        <v-window-item v-if="(campaign.system_notification_ids || []).length">
          <NotificationInfo />
        </v-window-item>
        <v-window-item v-if="campaign.total_participants">
          <ParticipantsInfo />
        </v-window-item>
        <v-window-item v-if="campaign.settings && superUser">
          <PerformanceInfo />
        </v-window-item>
      </v-window>
    </v-card>
    <v-dialog v-model="newDrawDialog" max-width="800">
      <b-card
        :title="translations.newDrawDialogTitle[lang]"
        @cancel="newDrawDialog = false"
        @submit="startCreateDrawCampaign"
        :loading="loading"
      >
        <v-col cols="12" md="12" class="d-flex flex-column pa-0">
          <span class="text-subtitle-1"
            >{{ translations.newDrawDialogTitle[lang] }}:
            <span class="font-weight-medium mb-n2 text-right">#{{ campaign.campaign_id }}</span>
            <span class="font-weight-bold"> {{ campaign.title }}</span></span
          >
          <v-divider class="ml-4 mr-4" vertical></v-divider>

          <v-col class="d-flex flex-column align-center justify-center">
            <span class="font-weight-regular"
              >{{ translations.chooseDates[lang] }} ({{ translations.optional[lang] }})</span
            >
            <v-row class="d-flex flex-row justify-space-between align-center mt-1">
              <v-col cols="12" lg="6">
                <v-menu v-model="fromDateMenu" :close-on-content-click="false" max-width="290">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="localizeDate(drawData.from_date)"
                      clearable
                      outlined
                      dense
                      :label="translations.from[lang] + translations.date[lang]"
                      hide-details="auto"
                      readonly
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="drawData.from_date = ''"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    dark
                    v-model="drawData.from_date"
                    :locale="locale"
                    @change="fromDateMenu = false"
                    :max="drawData.to_date || null"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" lg="6">
                <v-menu v-model="toDateMenu" :close-on-content-click="false" max-width="290">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="localizeDate(drawData.to_date)"
                      clearable
                      :label="translations.to[lang] + translations.date[lang]"
                      hide-details="auto"
                      readonly
                      prepend-inner-icon="mdi-calendar"
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="drawData.to_date = ''"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    dark
                    v-model="drawData.to_date"
                    :locale="locale"
                    @change="toDateMenu = false"
                    :min="drawData.from_date || null"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-col>
        </v-col>
      </b-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  mdiCalendarMonth,
  mdiChat,
  mdiBell,
  mdiEmail,
  mdiLock,
  mdiCached,
  mdiEmailOutline,
  mdiMessageReplyTextOutline,
  mdiMessageTextOutline,
  mdiBellOutline,
  mdiArrowRight,
  mdiCheckCircle,
  mdiCloseCircle,
  mdiGift,
  mdiAccountGroup,
  mdiTextBox,
  mdiArrowLeft,
  mdiBullhorn,
  mdiAccountArrowRight,
  mdiCalendar,
  mdiCartArrowDown,
  mdiCashPlus,
  mdiCalendarMultiple,
  mdiCalendarRefresh,
  mdiFilter,
  mdiRefresh,
  mdiFinance,
} from '@mdi/js';
import { mapMutations, mapState, mapActions } from 'vuex';

import ParticipantsInfo from '@/components/loyaltyPanel/viewCampaigns/ParticipantsInfo';
import translations from '@/utils/translations/loyaltyPanel/campaigns/viewCampaigns';
import debounce from 'lodash/debounce';
import GeneralInfo from '@/components/loyaltyPanel/viewCampaigns/GeneralInfoReport';
import RewardInfo from '@/components/loyaltyPanel/viewCampaigns/RewardInfo';
import NotificationInfo from '@/components/loyaltyPanel/viewCampaigns/NotificationInfo';
import PerformanceInfo from '@/components/loyaltyPanel/viewCampaigns/PerformanceInfo';

export default {
  name: 'ViewCampaignReport',

  mixins: [translations],

  components: {
    ParticipantsInfo,
    GeneralInfo,
    RewardInfo,
    NotificationInfo,
    PerformanceInfo,
  },

  data() {
    return {
      icons: {
        mdiCalendarMonth,
        mdiChat,
        mdiBell,
        mdiEmail,
        mdiLock,
        mdiCached,
        mdiEmailOutline,
        mdiMessageReplyTextOutline,
        mdiMessageTextOutline,
        mdiBellOutline,
        mdiArrowRight,
        mdiCheckCircle,
        mdiCloseCircle,
        mdiAccountGroup,
        mdiGift,
        mdiTextBox,
        mdiArrowLeft,
        mdiFilter,
        mdiRefresh,
        mdiFinance,
        types: {
          informative: mdiBullhorn,
          action: mdiAccountArrowRight,
          one_time_execution: mdiCalendar,
          repeat_execution: mdiCalendarRefresh,
          multiple_times_execution: mdiCalendarMultiple,
          new_transaction: mdiCashPlus,
          transaction_with_specific_products: mdiCartArrowDown,
        },
      },
      notificationIcons: {
        sms: mdiMessageTextOutline,
        mail: mdiEmailOutline,
        push: mdiBellOutline,
        viber: 'simple-icons:viber',
      },

      currentTab: 0,
      weekdays: [
        { name: 'sunday', value: false },
        { name: 'monday', value: false },
        { name: 'tuesday', value: false },
        { name: 'wednesday', value: false },
        { name: 'thursday', value: false },
        { name: 'friday', value: false },
        { name: 'saturday', value: false },
      ],
      newDrawDialog: false,
      drawData: {
        campaign_id: null,
        from_date: '',
        to_date: '',
      },
      fromDateMenu: false,
      toDateMenu: false,
    };
  },

  computed: {
    ...mapState(['loading', 'errorMessage', 'serverItemsLength', 'isSuperAdmin', 'superUser']),
    ...mapState('loyaltyPanel/campaigns/viewCampaigns', [
      'systemNotifications',
      'campaign',
      'campaignLoading',
      'campaignTypes',
      'transactionTypes',
      'campaignConditions',
      'clientCharacteristics',
    ]),

    lang() {
      return this.$route.params.lang;
    },
    locale() {
      if (this.lang == 'el') return 'el-GR';
      if (this.lang == 'it') return 'it-IT';
      return 'en-GB';
    },

    campaignId() {
      return this.$route.params.campaign_id;
    },

    hasReward() {
      return Boolean(
        this.campaign?.reward?.coupon?.coupon_id ||
          this.campaign?.reward?.reward_points ||
          this.campaign?.reward?.multi_points_ratio
      );
    },

    sections() {
      let sections = [{ value: 'general', icon: this.icons.mdiTextBox }];

      if (this.hasReward) {
        sections.push({ value: 'coupons', icon: this.icons.mdiGift });
      }
      if (this.campaign.system_notification_ids?.length) {
        sections.push({
          value: 'notifications',
          icon: this.icons.mdiBell,
        });
      }
      if (this.campaign.total_participants) {
        sections.push({
          value: 'participants',
          icon: this.icons.mdiAccountGroup,
        });
      }
      if (this.superUser && this.campaign.settings) {
        sections.push({
          value: 'statistics',
          icon: this.icons.mdiFinance,
        });
      }
      return sections;
    },

    campaignType() {
      let type = { name: '', value: {} };
      Object.keys(this.campaignTypes).forEach((key) => {
        let foundType = this.campaignTypes[key]?.campaign_types.find(
          (x) => this.campaign.campaign_type_id == x.campaign_type_id
        );
        if (foundType) {
          type.name = key;
          type.value = foundType;
        }
      });
      return type;
    },
  },

  watch: {
    ['campaign.campaign_type_id'](val) {
      if (val) {
        this.getCampaignConditions(val);
        this.getClientCharacteristics(val);
      }
    },
  },

  methods: {
    ...mapActions('loyaltyPanel/campaigns/viewCampaigns', [
      'getSingleCampaign',
      'getCampaignSystemNotifications',
      'getCampaignTypes',
      'getCampaignConditions',
      'getTransactionTypes',
      'getClientCharacteristics',
    ]),
    ...mapActions('loyaltyPanel/customers', ['getCustomerProfile']),
    ...mapMutations('loyaltyPanel/campaigns/newCampaign', [
      'setNewCampaign',
      'setStep',
      'setCanGoToNextStep',
      'setUploadingImage',
      'setMaxStep',
      'setSelectedCampaignsData',
    ]),
    ...mapMutations(['setDialog', 'setNotification']),

    localizeDate(date, short) {
      return date
        ? new Date(date).toLocaleString(this.locale, {
            weekday: short ? undefined : 'long',
            year: 'numeric',
            month: short ? 'numeric' : 'long',
            day: 'numeric',
          })
        : '-';
    },

    filterCampaignChannels(ids) {
      return this.systemNotifications.filter((e) => ids.includes(e.system_notification_id));
    },

    next() {
      this.currentTab = this.currentTab + 1 === this.sections.length ? 0 : this.currentTab + 1;
    },
    prev() {
      this.currentTab = this.currentTab - 1 < 0 ? this.sections.length - 1 : this.currentTab - 1;
    },
    openNewDrawDialog() {
      this.newDrawDialog = true;
    },
    startCreateDrawCampaign() {
      this.drawData.campaign_id = this.campaign.campaign_id;
      const campaignId = this.campaignTypes.draw.campaign_types.find(
        (type) => type.system === 'on_activate_execution'
      ).campaign_type_id;
      this.getCampaignConditions(campaignId);
      let draw = {
        category: 'draw',
        campaign_type_id: campaignId,
        campaign_condition_id: this.campaignConditions.find(
          (condition) => condition.system === 'from_older_campaign'
        ).campaign_condition_id,
        campaign_condition_id_options: [{ ...this.drawData }],
      };

      this.setCanGoToNextStep(false);
      this.setSelectedCampaignsData([{ ...this.campaign }]);
      this.setStep(3);
      this.setMaxStep(3);
      this.setUploadingImage(null);
      this.setNewCampaign(draw);
      this.$router.push(`/${this.lang}/loyaltyPanel/campaigns/new-campaign`);
    },

    fetchData() {
      this.getSingleCampaign(this.campaignId);

      if (!Object.keys(this.campaignTypes).length) {
        this.getCampaignTypes();
      }

      if (!Object.keys(this.transactionTypes).length) {
        this.getTransactionTypes();
      }
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>

<style>
.campaign-actions .v-skeleton-loader__actions {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  min-height: 42px;
}
.campaign-actions.category-loader .v-skeleton-loader__actions {
  height: 300px;
}
.campaign-actions .v-skeleton-loader__actions .v-skeleton-loader__button {
  height: auto;
  width: auto;
  min-width: 64px;
  width: 100%;
}

.campaign-actions.category-loader .v-skeleton-loader__actions .v-skeleton-loader__button {
  width: 400px;
  border-radius: 24px;
  margin-right: 40px !important;
}

.campaign-actions .v-skeleton-loader__actions .v-skeleton-loader__button:last-child {
  margin-right: 0 !important;
}
</style>

<style scoped>
.border-grey {
  border: 2px solid grey;
}

.b-outlined {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.icon-container {
  /* margin-top:20px; */
  height: 200px;
  width: 200px;
}
.select-category {
  cursor: pointer;
  transition: all 0.5s ease-out;
  background-color: white;
  min-width: 200px;
  font-size: 22px;
  color: gray;
  /* border: 1px solid black */
}
.select-category:hover {
  background-color: rgba(0, 0, 0, 0.2);
  color: black;
  /* font-size: 26px; */
}

.invisible {
  opacity: 0;
  z-index: 0;
}

.overlapped {
  position: absolute;
}

.transition-all {
  transition:
    all 1.5s,
    width 2s,
    max-width 1s,
    min-width 1s,
    margin-left 2s,
    margin-top 2.5s,
    background-position 1.5s,
    opacity 1s,
    background-color 2s;
}
.back-button {
  position: absolute;
  top: 45%;
  z-index: 10;
}
.mw-100 {
  min-width: 100% !important;
}
.mw-180 {
  min-width: 180px !important;
}
.b-outlined {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
